import type { ReactElement } from 'react';
import { formatPrice } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../../../bev-safe-text';

import type { Price } from './types';

export type PriceOptionsProps = {
	prices: Price[];
	locale: string;
	buttonLabel: string;
	selectedPriceId: string | null;
	t: (text: string, params?: (string | number)[]) => string;
	onSelect: (priceId: string) => void;
};

/**
 * Matches the Figma "Select Experience" list: bordered option cards (name + duration/pax line on the
 * left, price + availability dot on the right), selecting one highlights it - ported from
 * `ProductPrices.tsx` / `ProductPrice.tsx`, minus the modal each card used to open.
 */
export default function PriceOptions({
	prices,
	locale,
	buttonLabel,
	selectedPriceId,
	t,
	onSelect,
}: PriceOptionsProps): ReactElement {
	const visiblePrices =
		selectedPriceId != null ? prices.filter(p => p.id === selectedPriceId) : prices;

	return (
		<div
			className="flex max-h-[min(calc(100dvh-20rem),34rem)] flex-col gap-150 overflow-y-auto"
			data-testid="price-options"
		>
			{visiblePrices.map(price => {
				const isSelected = selectedPriceId === price.id;
				return (
					<button
						key={price.id}
						type="button"
						onClick={() => onSelect(price.id)}
						aria-pressed={isSelected}
						aria-label={[price.name, buttonLabel].filter(Boolean).join(' ')}
						data-testid={`price-option-${price.id}`}
						className={cn(
							'flex flex-col gap-100 rounded-sm border p-200 text-left transition-colors',
							isSelected
								? 'border-border-strong bg-neutral-100'
								: 'border-border-subtle bg-surface-default hover:bg-neutral-100'
						)}
					>
						<div className="flex flex-wrap items-start justify-between gap-200">
							<span className="text-subtitle2 text-text-heading">{price.name}</span>
							{price.complimentary ? (
								<span className="text-subtitle2 text-text-heading">{t('Complimentary')}</span>
							) : (
								<span className="text-subtitle2 whitespace-nowrap text-text-heading">
									{formatPrice({
										value: price.amount,
										currencyCode: price.currencyCode,
										locale,
										decimalPlaces: 0,
									})}
									{price.type === 'PER_PERSON' && (
										<span className="text-caption text-text-secondary"> {t('/person')}</span>
									)}
								</span>
							)}
						</div>

						<div className="flex items-center justify-between gap-200">
							<span className="text-body3 text-text-secondary">
								<BevSafeText>{price.subtitle}</BevSafeText>
							</span>
							{/* gap-050 isn't a registered class (no 5px token exists) - silently
							    rendered zero gap. Nearest real token, matching the same
							    dot+label gap experiences-grid's AvailabilityIndicator uses. */}
							{price.availabilitySummary?.message && (
								<span className="text-body3 flex items-center gap-060 text-text-secondary">
									<span
										aria-hidden
										className={cn(
											'h-080 w-080 rounded-full',
											price.availabilitySummary.iconColor === 'GREEN'
												? 'bg-success-icon'
												: 'bg-warning-icon'
										)}
									/>
									<BevSafeText>{price.availabilitySummary.message}</BevSafeText>
								</span>
							)}
						</div>

						{price.note && (
							<span className="text-caption text-text-secondary">{t(price.note)}</span>
						)}
					</button>
				);
			})}
		</div>
	);
}
